import {
    BlockStack,
    Button,
    DataTable,
    Typography,
    type DataTableColumn,
} from '@pv-frontend/ui-components-web';

/**
 * Plain view model for a selectable vault in the cohort picker — a trimmed
 * projection of a `vaultSearch` result, decoupled from Relay's generated types
 * so this component is unit-testable without a Relay harness.
 */
export interface VaultOptionView {
    vaultId: string;
    name: string;
    identifier: string;
    ownerEmail: string | null;
}

export interface VaultCohortPickerProps {
    /** The current (server-filtered) search results. */
    vaults: readonly VaultOptionView[];
    /** Vault ids currently in the cohort — selection persists across searches. */
    selectedIds: ReadonlySet<string>;
    onToggle: (vault: VaultOptionView) => void;
    /** Live search term, for the empty-state copy. */
    search: string;
}

/**
 * The multi-select vault list used to assemble an experiment cohort. Reuses the
 * tenant pillar's `vaultSearch`; each row toggles in or out of the cohort with an
 * Add / Remove button (selection lives in the parent so it survives re-searches).
 */
export function VaultCohortPicker({ vaults, selectedIds, onToggle, search }: VaultCohortPickerProps) {
    const columns: DataTableColumn<VaultOptionView>[] = [
        {
            key: 'name',
            header: 'Vault',
            render: (vault) => (
                <BlockStack gap="050">
                    <Typography variant="body-sm" fontWeight="medium">
                        {vault.name}
                    </Typography>
                    <Typography variant="body-sm" tone="text-secondary">
                        {vault.identifier}
                    </Typography>
                </BlockStack>
            ),
        },
        {
            key: 'owner',
            header: 'Owner',
            render: (vault) =>
                vault.ownerEmail ? (
                    <Typography variant="body-sm">{vault.ownerEmail}</Typography>
                ) : (
                    <Typography variant="body-sm" tone="text-secondary">
                        —
                    </Typography>
                ),
        },
        {
            key: 'select',
            header: 'In cohort',
            align: 'end',
            render: (vault) => {
                const selected = selectedIds.has(vault.vaultId);
                return (
                    <Button
                        size="slim"
                        variant={selected ? 'tertiary' : 'block'}
                        onPress={() => onToggle(vault)}
                        testID={`toggle-${vault.vaultId}`}
                    >
                        {selected ? 'Remove' : 'Add'}
                    </Button>
                );
            },
        },
    ];

    const empty = search.trim() ? `No vaults match "${search.trim()}".` : 'No vaults found.';

    return (
        <DataTable
            columns={columns}
            rows={vaults}
            keyExtractor={(vault) => vault.vaultId}
            emptyState={empty}
            testID="cohort-vaults"
        />
    );
}
